import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SparklesIcon, ScaleIcon, BookmarkIcon, BookmarkXIcon, BellIcon, SearchIcon } from 'lucide-react';
import { ListingCard } from '../components/ListingCard';
const tabs = ['Cars', 'Properties', 'Jobs', 'Services', 'Products'];
interface SavedItem {
  id: string;
  title: string;
  price: string;
  location: string;
  images: string[];
  specs: {
    label: string;
    value: string;
  }[];
  matchScore: number;
  sellerType: 'Dealer' | 'Individual' | 'Verified Business';
  isSaved: boolean;
  available: boolean;
}
const initialSaved: SavedItem[] = [{
  id: '1',
  title: 'Toyota Land Cruiser Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: ['https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800'],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }],
  matchScore: 98,
  sellerType: 'Verified Business',
  isSaved: true,
  available: true
}, {
  id: '2',
  title: 'Toyota Prado TXL 2019 Clean',
  price: 'AED 89,000',
  location: 'Abu Shagara, Sharjah',
  images: ['https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800'],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '85,000 km'
  }],
  matchScore: 94,
  sellerType: 'Individual',
  isSaved: true,
  available: false
}];
export function Saved() {
  const [activeTab, setActiveTab] = useState('Cars');
  const [items, setItems] = useState<SavedItem[]>(initialSaved);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const toggleCompare = (id: string) => {
    if (selectedForCompare.includes(id)) {
      setSelectedForCompare(selectedForCompare.filter(itemId => itemId !== id));
    } else if (selectedForCompare.length < 3) {
      setSelectedForCompare([...selectedForCompare, id]);
    }
  };
  const unsaveItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
    setSelectedForCompare(prev => prev.filter(itemId => itemId !== id));
  };
  return <div className="flex-1 w-full overflow-y-auto px-4 pt-5 pb-28 relative" data-id="element-3050">
      <div className="mb-5" data-id="element-3051">
        <h1 className="font-display font-bold text-2xl text-dark-navy mb-1" data-id="element-3052">
          Saved Items
        </h1>
        <p className="text-slate-500 text-sm" data-id="element-3053">
          Your favorite listings across all categories.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto pb-3 mb-4 hide-scrollbar gap-2 -mx-4 px-4" data-id="element-3054">
        {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 whitespace-nowrap text-sm font-medium rounded-full transition-all ${activeTab === tab ? 'bg-dark-navy text-white' : 'bg-white border border-slate-200 text-slate-600'}`} data-id="element-3055">
            {tab}
          </button>)}
      </div>

      {/* AI Prompt Banner */}
      <div className="bg-gradient-to-r from-primary-indigo/5 to-ai-purple/5 rounded-2xl p-3.5 mb-5 border border-primary-indigo/10 flex items-center gap-3" data-id="element-3056">
        <div className="p-2 bg-white rounded-xl shadow-sm flex-shrink-0" data-id="element-3057">
          <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-3058" />
        </div>
        <p className="text-slate-700 text-[13px] font-medium leading-snug" data-id="element-3059">
          Select up to 3 items to let AI compare them for you.
        </p>
      </div>

      {items.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-20" data-id="element-3060">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4" data-id="element-3061">
            <BookmarkIcon className="w-6 h-6 text-slate-400" data-id="element-3062" />
          </div>
          <h3 className="font-display font-bold text-dark-navy mb-1" data-id="element-3063">
            No saved items yet
          </h3>
          <p className="text-slate-500 text-sm max-w-[240px]" data-id="element-3064">
            Tap the bookmark on any listing to keep it here for later.
          </p>
        </div> : <div className="grid grid-cols-1 gap-5" data-id="element-3065">
          <AnimatePresence mode="popLayout" data-id="element-3066">
            {items.map((item, idx) => {
          const isSelected = selectedForCompare.includes(item.id);
          return <motion.div key={item.id} layout initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.92,
            transition: {
              duration: 0.2
            }
          }} transition={{
            delay: idx * 0.08
          }} className="relative" data-id="element-3067">
                  {/* Compare order badge */}
                  <div className={`absolute -left-2 -top-2 z-20 transition-all ${isSelected ? 'opacity-100' : 'opacity-0'}`} data-id="element-3068">
                    <div className="w-6 h-6 bg-primary-indigo text-white rounded-full flex items-center justify-center font-bold text-xs shadow-md border-2 border-white" data-id="element-3069">
                      {selectedForCompare.indexOf(item.id) + 1}
                    </div>
                  </div>

                  {/* Unsave button — always available */}
                  <button onClick={e => {
              e.stopPropagation();
              unsaveItem(item.id);
            }} aria-label={`Remove ${item.title} from saved`} className="absolute right-3 top-3 z-20 w-9 h-9 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center text-rose-500 active:scale-90 transition-transform border border-white/40" data-id="element-3070">
                    <BookmarkXIcon className="w-[18px] h-[18px]" data-id="element-3071" />
                  </button>

                  <div className={`rounded-2xl transition-all ${isSelected ? 'ring-2 ring-primary-indigo ring-offset-2' : ''}`} onClick={() => item.available && toggleCompare(item.id)} data-id="element-3072">
                    <div className="relative" data-id="element-3073">
                      <div className={item.available ? '' : 'grayscale opacity-60 pointer-events-none select-none'} data-id="element-3074">
                        <ListingCard {...item} data-id="element-3075" />
                      </div>

                      {/* Unavailable overlay */}
                      {!item.available && <>
                          <div className="absolute inset-0 rounded-2xl bg-dark-navy/5 pointer-events-none" data-id="element-3076" />
                          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-dark-navy text-white text-[11px] font-bold tracking-wide shadow-md" data-id="element-3077">
                            No longer available
                          </div>
                        </>}
                    </div>
                  </div>

                  {/* Footer action row */}
                  {item.available ? <div className="mt-2.5 text-center" data-id="element-3078">
                      <button onClick={() => toggleCompare(item.id)} className={`text-[13px] font-medium transition-colors ${isSelected ? 'text-primary-indigo' : 'text-slate-500'}`} data-id="element-3079">
                        {isSelected ? 'Selected for comparison' : 'Select to compare'}
                      </button>
                    </div> : <div className="mt-2.5 grid grid-cols-2 gap-2" data-id="element-3080">
                      <button className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-dark-navy text-white text-[13px] font-semibold active:scale-[0.98] transition-transform" data-id="element-3081">
                        <SearchIcon className="w-4 h-4" data-id="element-3082" />
                        Find similar
                      </button>
                      <button className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-[13px] font-semibold active:scale-[0.98] transition-transform" data-id="element-3083">
                        <BellIcon className="w-4 h-4" data-id="element-3084" />
                        Notify me
                      </button>
                    </div>}
                </motion.div>;
        })}
          </AnimatePresence>
        </div>}

      {/* Sticky compare bar */}
      {selectedForCompare.length > 0 && <motion.div initial={{
      y: 80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="fixed left-1/2 -translate-x-1/2 bottom-6 w-[88%] max-w-[340px] z-40" data-id="element-3085">
          <button className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-primary-indigo to-ai-purple text-white rounded-2xl font-semibold shadow-[0_12px_40px_rgb(67,56,202,0.4)] active:scale-[0.98] transition-transform" data-id="element-3086">
            <ScaleIcon className="w-5 h-5" data-id="element-3087" />
            Ask Talab to compare ({selectedForCompare.length})
          </button>
        </motion.div>}
    </div>;
}